import { apiFetch } from "../client.js";
import { state, patch } from "../../state/store.js";
import { sortChatsForList } from "../../views/Chat.helpers.ts";

export async function fetchChats({ limit = 2000, search = "" } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (search) qs.set("search", search);
  const data = await apiFetch(`/whatsapp/chats?${qs.toString()}`);
  patch({ chats: sortChatsForList(data?.chats || []) });
  return data;
}

/**
 * @param {string} chatJid
 * @param {{ beforeTs?: number | string, limit?: number, aliasJids?: string[] }} [opts]
 */
export async function fetchMessages(chatJid, opts = {}) {
  const { beforeTs, limit = 50, aliasJids = [] } = opts;
  const qs = new URLSearchParams({ limit: String(limit) });
  if (beforeTs) qs.set("beforeTs", String(beforeTs));
  for (const jid of aliasJids) {
    if (jid && jid !== chatJid) qs.append("aliasJid", String(jid));
  }
  const data = await apiFetch(`/whatsapp/chats/${encodeURIComponent(chatJid)}/messages?${qs.toString()}`);
  return data?.messages || [];
}

export async function fetchCentralLead(chatJid) {
  return apiFetch(`/whatsapp/chats/${encodeURIComponent(chatJid)}/central-lead`);
}

export async function markChatRead(chatJid) {
  await apiFetch(`/whatsapp/chats/${encodeURIComponent(chatJid)}/read`, { method: "POST" });
  const idx = state.chats.findIndex((chat) => chat.chatJid === chatJid);
  if (idx >= 0) {
    const next = [...state.chats];
    next[idx] = { ...next[idx], unreadCount: 0 };
    patch({ chats: next });
  }
}

export async function archiveChat(chatJid) {
  await apiFetch(`/whatsapp/chats/${encodeURIComponent(chatJid)}/archive`, { method: "POST" });
  applyArchivedPatch(chatJid, true);
}

export async function unarchiveChat(chatJid) {
  await apiFetch(`/whatsapp/chats/${encodeURIComponent(chatJid)}/unarchive`, { method: "POST" });
  applyArchivedPatch(chatJid, false);
}

export async function bulkArchiveChats(chatJids, archived = true) {
  if (!Array.isArray(chatJids) || chatJids.length === 0) return { ok: true, updated: 0 };
  const data = await apiFetch("/whatsapp/chats/bulk-archive", {
    method: "POST",
    body: JSON.stringify({ chatJids, archived }),
  });
  const set = new Set(chatJids);
  patch({ chats: state.chats.map((chat) => (set.has(chat.chatJid) ? { ...chat, archived } : chat)) });
  return data;
}

export async function pinChat(chatJid) {
  const data = await apiFetch(`/whatsapp/chats/${encodeURIComponent(chatJid)}/pin`, { method: "POST" });
  applyPinPatch(chatJid, data?.pinnedAt || new Date().toISOString());
  return data;
}

export async function unpinChat(chatJid) {
  await apiFetch(`/whatsapp/chats/${encodeURIComponent(chatJid)}/pin`, { method: "DELETE" });
  applyPinPatch(chatJid, null);
}

export async function renameChat(chatJid, name) {
  const data = await apiFetch(`/whatsapp/chats/${encodeURIComponent(chatJid)}/name`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
  const idx = state.chats.findIndex((chat) => chat.chatJid === chatJid);
  if (idx >= 0) {
    const next = [...state.chats];
    next[idx] = { ...next[idx], pushName: name };
    patch({ chats: next });
  }
  return data;
}

export async function syncChatHistory(chatJid, { limit = 200 } = {}) {
  return apiFetch(`/whatsapp/chats/${encodeURIComponent(chatJid)}/sync?limit=${limit}`, { method: "POST" });
}

export async function syncChatMetadata() {
  const data = await apiFetch("/whatsapp/chats/sync-metadata", { method: "POST" });
  if (data?.ok) await fetchChats();
  return data;
}

function applyArchivedPatch(chatJid, archived) {
  const idx = state.chats.findIndex((chat) => chat.chatJid === chatJid);
  if (idx < 0) return;
  const next = [...state.chats];
  next[idx] = { ...next[idx], archived };
  patch({ chats: next });
}

function applyPinPatch(chatJid, pinnedAt) {
  const idx = state.chats.findIndex((chat) => chat.chatJid === chatJid);
  if (idx < 0) return;
  const next = [...state.chats];
  next[idx] = { ...next[idx], pinnedAt };
  patch({ chats: sortChatsForList(next) });
}
